import Flex from "common/Boxes/Flex";
import {
  BACKTESTING_TIMEFRAMES,
  BacktestingTimeframe,
  LanguageNameSpaces,
} from "@shared/types";
import ChangeTag from "common/Tags/ChangeTag";
import RiskChart from "features/newMarketplace/common/RiskChart";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import ChartCardLayout from "common/Layouts/ChartCardLayout";
import React from "react";
import LineChart from "common/Charts/LineChart/LineChart";
import useOverviewStatistics from "@shared/features/newMarketplace/hooks/useOverviewStatistics";
import Col from "antd/es/col";
import Row from "antd/es/row";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import NewLookOverviewStatistics from "features/newMarketplace/NewLook/NewLookOverviewStatistics.tsx";

const ClassicOverviewStatistics = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const {
    lineChartData,
    timeRange,
    setTimeRange,
    timeRanges,
    getPnlStatisticData,
    groupOverview,
    getGroupOverviewQuery,
    graphTooltipProps,
  } = useOverviewStatistics({ isMobile });

  if (!groupOverview) return null;

  const getPnlStatistic = (timeframe: BacktestingTimeframe) => {
    const data = getPnlStatisticData(timeframe);
    return (
      <Flex key={timeframe} $flexDirection="column" $justifyContent="center">
        <Title level={4} type="secondary">
          {data.title}
        </Title>
        <ChangeTag
          value={data.change}
          suffix="%"
          style={{
            margin: "8px 0 0",
            fontSize: typography.fontSizes.S,
            lineHeight: "20px",
          }}
          arrowStyle={{ fontSize: 9 }}
        />
      </Flex>
    );
  };

  const lineChart = (
    <LineChart
      data={lineChartData.data}
      loading={getGroupOverviewQuery?.isFetching}
      isSuccess={getGroupOverviewQuery?.isSuccess}
      precision={lineChartData.precision}
      valueSuffix={lineChartData.valueSuffix}
    />
  );

  const chart = (
    <ChartCardLayout
      chart={lineChart}
      selectedTimeRange={timeRange}
      setTimeRange={setTimeRange}
      buttonSize="medium"
      autoLayoutHeight={true}
      timeRanges={timeRanges}
      mobileBreakpoint={Infinity}
    />
  );

  return (
    <>
      <Row gutter={[0, 24]}>
        {BACKTESTING_TIMEFRAMES.slice(1).map((timeframe) => {
          return (
            <Col key={timeframe} span={isMobile ? 12 : 6}>
              {getPnlStatistic(timeframe)}
            </Col>
          );
        })}
        <Col span={isMobile ? 12 : 6}>
          <RiskChart
            scoreBreakdown={groupOverview?.backtesting_score_breakdown}
          />
        </Col>
      </Row>
      <div style={{ height: isMobile ? 240 : 300, margin: "24px 0" }}>
        <Title level={4} type="secondary">
          {t("overview.accumulated_net_profit")}
          <HelperTooltip {...graphTooltipProps} />
        </Title>
        {chart}
      </div>
    </>
  );
};

const OverviewStatistics = () => {
  const { isNewLook } = useTheme();
  return isNewLook ? (
    <NewLookOverviewStatistics />
  ) : (
    <ClassicOverviewStatistics />
  );
};

export default OverviewStatistics;
